import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { initIndexDB } from '@thd-olt-global/thd-storage-utils';
import { AssistantProvider } from './AssistantProvider';
import { DrawerProvider } from './DrawerProvider';
import { MagicApronAssistant } from './MagicApronAssistant';
import { getPageType, getParentPagePath } from '../../utils/assistant-context-utils';

const indexDBConfig = {
  version: 2,
  stores: {
    sessions: {
      storeConfig: { keyPath: 'sessionId' },
      indexes: [{ name: 'sessionId', unique: true }],
    },
    messages: {
      storeConfig: { keyPath: 'id', autoIncrement: true },
      indexes: [{ name: 'id', unique: true }],
    }
  }
};

initIndexDB('assistantBotDB', indexDBConfig).catch(() => {});

/**
 * The AssistantWrapper component is responsible for providing the
 * context and configuration for the Assistant.
 * @param {Object} props - The component props.
 * @param {boolean} props.withPersistence - Flag to enable persistence.
 * @param {boolean} props.force - Flag to force rendering the assistant.
 * @returns {JSX.Element} The rendered component.
 */

export const AssistantWrapper = ({ withPersistence, force }) => {

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const isEnabledMobile = useConfigService('fs-prop:magic-apron-assistant-mw-enabled');
  const isEnabledDT = useConfigService('fs-prop:magic-apron-assistant-dt-enabled');
  // this is to ensure we are only rendering assistant on the correct page types that are in scope
  const isAssistantCompatiblePage = !(getPageType() === 'Home' && getParentPagePath() !== '/');

  if ((!isAssistantCompatiblePage || (!(isEnabledMobile && isMobile)
    && !(isEnabledDT && !isMobile))) && !force) return null;

  return (
    <AssistantProvider withPersistence={withPersistence}>
      <DrawerProvider>
        <MagicApronAssistant />
      </DrawerProvider>
    </AssistantProvider>
  );
};

AssistantWrapper.propTypes = {
  withPersistence: PropTypes.bool,
  force: PropTypes.bool,
};

AssistantWrapper.defaultProps = {
  withPersistence: true,
  force: false
};

AssistantWrapper.dataModel = extend(MagicApronAssistant);
